<template>
  <div>
    <div class="flex flex-wrap items-center justify-between pb-4 py-1">
      <div v-for="(group, index) in filteredBloodGroups" :key="index" class="mb-6">
        <h2 class="text-center font-semibold text-lg">{{ group.name }}</h2>
        <table class="w-full text-left rtl:text-right text-gray-700 border">
          <thead>
            <tr class="bg-gray-100">
              <th scope="col" class="px-4 py-2">
                <div class="flex items-center text-sm">Product</div>
              </th>
              <th scope="col" class="px-4 py-2">
                <div class="flex items-center text-sm">Supply</div>
              </th>
              <th scope="col" class="px-4 py-2">
                <div class="flex items-center text-sm">Demand</div>
              </th>
              <th scope="col" class="px-4 py-2">
                <div class="flex items-center text-sm">Target Deficit</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, productIndex) in bloodProducts" :key="productIndex" class="border-b">
              <td class="px-4 py-2">{{ product.name }}</td>
              <td class="px-4 py-2">{{ getSupply(product.unit_product_id, group.blood_group_id) }}</td>
              <td class="px-4 py-2">{{ getDemand(product.unit_product_id, group.blood_group_id) }}</td>
              <td class="px-4 py-2">{{ getTargetDeficit(product.unit_product_id, group.blood_group_id) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'; // Import your API service

export default {
  name: 'LiveCounterTallyPage',
  data() {
    return {
      bloodGroups: [],
      bloodProducts: [],
      bloodData: {}, // Data structure to store supply, demand, and deficit
    };
  },
  mounted() {
    this.user = this.$store.getters["auth/user"];
    this.fetchBloodGroups();
    this.fetchBloodProducts();
    this.fetchSupplyDemandData(); // New method to fetch the blood data (supply/demand/deficit)
    this.facility_logo = this.user.user_roles[0]?.facility_department?.facility?.logo;
  },
  computed: {
    filteredBloodGroups() {
      // Filter out blood groups named "Unknown"
      return this.bloodGroups.filter(group => group.name !== 'Unknown');
    }
  },
  methods: {
    async fetchBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response || [];
      } catch (error) {
        console.error('Error fetching blood groups:', error);
      }
    },
    async fetchBloodProducts() {
      try {
        const response = await api.getUnitProducts();
        this.bloodProducts = response || [];
      } catch (error) {
        console.error('Error fetching blood products:', error);
      }
    },
    async fetchSupplyDemandData() {
      try {
        const response = await api.getSupplyDemandDeficit();
        // console.log('Backend response:', response);
        this.bloodData = response || {};
      } catch (error) {
        console.error('Error fetching supply and demand data:', error);
      }
    },
    getSupply(productId, groupId) {
      return this.bloodData[groupId]?.[productId]?.supply ?? 0;
    },
    getDemand(productId, groupId) {
      return this.bloodData[groupId]?.[productId]?.demand ?? 0;
    },
    getTargetDeficit(productId, groupId) {
      const deficit = this.bloodData[groupId]?.[productId]?.deficit ?? 0;
      return deficit !== 0 ? -Math.abs(deficit) : 0;
    },
    async handleDownloadTallyReport() {
  const printWindow = document.createElement('iframe');
  printWindow.style.position = 'absolute';
  printWindow.style.width = '0';
  printWindow.style.height = '0';
  document.body.appendChild(printWindow);

  try {
    // Prepare table headers (products with sub-headers for Supply, Demand, Target Deficit)
    const tableHeaders = `
      <tr class="bg-gray-100">
        <th class="px-2 py-1" rowspan="2">Blood Group</th> <!-- Reduced padding -->
        ${this.bloodProducts.map(product => `
          <th class="px-2 py-1 bg-gray-100" colspan="3">${product.name}</th>
        `).join('')}
      </tr>
      <tr class="bg-gray-100">
        ${this.bloodProducts.map(() => `
          <th class="px-2 py-1">Supply</th>
          <th class="px-2 py-1">Demand</th>
          <th class="px-2 py-1">Deficit</th>
        `).join('')}
      </tr>
    `;

    // Prepare table rows (blood groups with Supply, Demand, and Target Deficit data)
    const tableRows = this.filteredBloodGroups.map(group => {
      const rowCells = this.bloodProducts.map(product => {
        const supply = this.getSupply(product.unit_product_id, group.blood_group_id) || 0;
        const demand = this.getDemand(product.unit_product_id, group.blood_group_id) || 0;
        const targetDeficit = this.getTargetDeficit(product.unit_product_id, group.blood_group_id) || 0;
        return `
          <td class="px-2 py-1 border">${supply}</td>
          <td class="px-2 py-1 border">${demand}</td>
          <td class="px-2 py-1 border">${targetDeficit}</td>
        `;
      }).join('');
      return `
        <tr class="border-b">
          <td class="px-2 py-1 font-semibold">${group.name}</td>
          ${rowCells}
        </tr>
      `;
    }).join('');

    // Prepare print content
    const printContent = `
      <div class="page-container">
        <div class="report-container">
          <div class="logo-container">
            <img src="${this.facility_logo}" alt="Hospital Logo" class="logo">
          </div>
          <br><br>
          <h1 class="text-lg font-bold mb-4">Inventory Tally Report</h1>
          <p class="mb-4">Generated on: ${new Date().toLocaleString()}</p>
          
          <div class="table-container">
            <table class="w-full text-left border-collapse">
              <thead>
                ${tableHeaders}
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;

    // Prepare the full document for printing
    const printDocument = `
      <html>
      <head>
        <title>Inventory Tally Report</title>
        <style>
          @media print {
            @page {
              size: A4 landscape; /* Landscape orientation */
              margin: 1cm; /* Reduced margin to maximize space */
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            line-height: 1.4; /* Slightly reduced line height */
            color: #333;
            margin: 0;
            padding: 0;
          }
          .logo {
            width: 100px; /* Reduced logo size to save space */
            float: right;
          }

          .page-container {
            padding: 10px; /* Reduced padding */
          }

          .report-container {
            max-width: 100%;
            margin: 0 auto;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 9px; /* Reduced font size to fit more content */
          }

          th, td {
            border: 1px solid #ddd;
            padding: 2px; /* Reduced padding */
            text-align: center;
            white-space: nowrap; /* Prevent text wrapping */
          }

          th {
            background-color: #F3F4F6;
            font-weight: bold;
          }

          .signatures {
            margin-top: 40px;
            page-break-inside: avoid;
            font-size: 9px; /* Reduced font size */
          }

          .signature-line {
            margin-bottom: 20px;
          }

          @media print {
            .status-badge {
              border: 1px solid #ddd;
            }
          }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
      </html>
    `;

    // Open the print document
    printWindow.contentDocument.open();
    printWindow.contentDocument.write(printDocument);
    printWindow.contentDocument.close();

    // Print when the document is loaded
    printWindow.contentWindow.onload = function () {
      printWindow.contentWindow.print();
      // Remove the iframe after printing
      setTimeout(() => {
        document.body.removeChild(printWindow);
      }, 1000);
    };
  } catch (error) {
    console.error('Error generating report:', error);
  }
},
  }
}
</script>

<style scoped>
/* Add your scoped styles here */
</style>
